import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from 'src/environments/environment';

interface PendingUserContext {
  userId: string;
  accountId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AppInsightsService {
  appInsights: ApplicationInsights | null = null;
  private readonly enabled: boolean;
  private consentGranted = false;
  private pendingUserContext: PendingUserContext | null = null;

  constructor() {
    const env = environment as { instrumentationKey?: string; connectionString?: string };
    const key = env.instrumentationKey;
    const connectionString = env.connectionString;
    this.enabled = !!(connectionString || key);

    if (!this.enabled) {
      return;
    }

    const config: Record<string, unknown> = {
      enableAutoRouteTracking: true,
      enableAutoCollectExceptions: true,
      enableCorsCorrelation: true,
      cookieCfg: { enabled: false },
    };

    if (connectionString) {
      config.connectionString = connectionString;
    } else if (typeof key === 'string' && key.includes('IngestionEndpoint')) {
      config.connectionString = key;
    } else {
      config.instrumentationKey = key;
    }

    this.appInsights = new ApplicationInsights({ config });
    // this.appInsights.loadAppInsights();

    const envName = (environment as { environmentName?: string }).environmentName;
    if (envName) {
      this.appInsights.addTelemetryInitializer(
        (envelope: {
          data?: { baseData?: { properties?: Record<string, string> } };
          baseData?: { properties?: Record<string, string> };
        }) => {
          const baseData = envelope.baseData ?? envelope.data?.baseData ?? {};
          baseData.properties = { ...baseData.properties, environment: envName };
          if (envelope.baseData) {
            envelope.baseData = baseData;
          } else if (envelope.data) {
            envelope.data.baseData = baseData;
          } else {
            envelope.data = { baseData };
          }
          return true;
        }
      );
    }
  }

  grantConsent(): void {
    this.consentGranted = true;
    this.appInsights?.getCookieMgr()?.setEnabled(true);

    if (this.pendingUserContext) {
      const { userId, accountId } = this.pendingUserContext;
      this.appInsights?.setAuthenticatedUserContext(userId, accountId, true);
      this.pendingUserContext = null;
    }
  }

  denyConsent(): void {
    this.consentGranted = false;
    this.appInsights?.getCookieMgr()?.setEnabled(false);
    this.appInsights?.clearAuthenticatedUserContext();
    this.pendingUserContext = null;
  }

  setAuthenticatedUserContext(userId: string, accountId?: string): void {
    if (this.consentGranted) {
      this.appInsights?.setAuthenticatedUserContext(userId, accountId, true);
    } else {
      this.pendingUserContext = { userId, accountId };
    }
  }

  clearAuthenticatedUserContext(): void {
    this.appInsights?.clearAuthenticatedUserContext();
    this.pendingUserContext = null;
  }

  logPageView(name?: string, url?: string): void {
    this.appInsights?.trackPageView({ name, uri: url });
  }

  logEvent(name: string, properties?: Record<string, unknown>): void {
    this.appInsights?.trackEvent({ name }, properties);
  }

  logMetric(name: string, average: number, properties?: Record<string, unknown>): void {
    this.appInsights?.trackMetric({ name, average }, properties);
  }

  logException(exception: Error, severityLevel?: number): void {
    this.appInsights?.trackException({ exception, severityLevel });
  }

  logTrace(message: string, properties?: Record<string, unknown>): void {
    this.appInsights?.trackTrace({ message }, properties);
  }
}
